import { InputFormControl, SelectFormControl } from 'BrokerTsLib/src/components'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { isOtherMake } from 'BrokerTsLib/src/validation'
import { useEffect, useMemo, useRef } from 'react'

import { FormRow } from '@/containers/forms/FormRow'
import { useAuth } from '@/context/AuthContext'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useGetMakers, useGetModels, useGetModelsBase } from '@/hooks/useVehicle'

export const VehicleModelSelection = ({
  isDisabled = false
}: {
  isDisabled?: boolean
}) => {
  const {
    control,
    setValue,
    watch
  } = useFormContext<InsuranceParamsDto>()

  const { insuranceParamsId } = useInsuranceParams()
  const { user } = useAuth()

  const brokerMakeModelId = watch('vehicle.brokerMakeModelId')
  const fuel = watch('vehicle.palivo')
  const category = watch('vehicle.druh')
  const make = watch('vehicle.znacka')
  const modelBase = watch('vehicle.zakladniModel')
  const model = watch('vehicle.model')

  // broker make model ciselnik pouziju, jen pokud ho ma partner povoleny (PartnerSettings.UseBrokerMakeModel — stejny flag na backendu prepina novy REST VINONLINE) a jde o nove paramsy, nebo existujici s jiz vybranym vozidlem z tohoto ciselniku. 
  // Detekce jen pri prvnim loadu  
  const useBrokerMakeModel = useRef(!!brokerMakeModelId || (!insuranceParamsId && (user?.useBrokerMakeModel ?? false)))

  const { data: makersData, isLoading: isLoadingMakers } = useGetMakers({ fuelId: fuel ?? 0, categoryId: category ?? 0, useBrokerMakeModel: useBrokerMakeModel.current })
  const { data: modelBaseData, isLoading: usLoadingModelBases, isFetched } = useGetModelsBase({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: make ?? '', useBrokerMakeModel: useBrokerMakeModel.current })
  // pocet vsech modelu znacky (bez filtru na zakladni model) kvuli tooMuchModels
  // pokud neni vybran zakladni model, jde o stejny dotaz jako modelsData a React Query jej nevola znovu
  const { data: modelsNotByBaseData } = useGetModels({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: make ?? '', useBrokerMakeModel: useBrokerMakeModel.current })
  const { data: modelsData, isLoading: isLoadingModels } = useGetModels({ fuelId: fuel ?? 0, categoryId: category ?? 0, maker: make ?? '', modelBase, useBrokerMakeModel: useBrokerMakeModel.current })

  // if model is not within modelsData, add it to options
  const modelOptions = useMemo(() => {
    const modelOptions = modelsData?.map((item) => ({ value: useBrokerMakeModel.current ? item.brokerMakeModelId : item.model, label: item.model }))
    if (!useBrokerMakeModel.current && model && modelsData && !modelsData.find(x => x.model == model)) {
      modelOptions?.unshift({ value: model, label: model })
    }
    return modelOptions
  }, [modelsData, model, useBrokerMakeModel])

  const tooMuchModels = (modelsNotByBaseData?.length ?? 0) > 600 && (modelBaseData?.length ?? 0) > 1

  // znacka je povinna pro vsechny druhy vozidla - minimalne musi byt vybrano OSTATNÍ
  const makeRequired = !!category

  const showMakeText = isOtherMake(make)

  const onMakeChange = (value: string) => {
    setValue('vehicle.znackaText', '')
    setValue('vehicle.zakladniModel', '')
    setValue('vehicle.model', '')
    setValue('vehicle.brokerMakeModelId', null)
  }

  const onModelBaseChange = (value: string) => {
    setValue('vehicle.model', '')
    setValue('vehicle.brokerMakeModelId', null)
  }

  useEffect(() => {
    if (makersData?.length && !makersData?.some((item) => item.toUpperCase() === make?.toUpperCase()))
      setValue('vehicle.znacka', '')
  }, [makersData])

  useEffect(() => {
    if (make != 'OSTATNÍ')
      setValue('vehicle.znackaText', '')
  }, [make])

  useEffect(() => {
    if (modelBaseData?.length && !modelBaseData?.some((item) => item === modelBase))
      setValue('vehicle.zakladniModel', '')
  }, [modelBaseData])

  useEffect(() => {
    if (modelsData?.length === 1) {
      setValue('vehicle.model', modelsData[0].model, { shouldValidate: true })
      setValue('vehicle.brokerMakeModelId', modelsData[0].brokerMakeModelId)
    }
    else if (modelsData?.length && (useBrokerMakeModel.current
      ? !modelsData?.some((item) => item.brokerMakeModelId === brokerMakeModelId)
      : !modelsData?.some((item) => item.model === model)
    )) {
      setValue('vehicle.model', '')
      setValue('vehicle.brokerMakeModelId', null)
    }
    else if (modelsData?.length == 0 && modelBase)
      setValue('vehicle.zakladniModel', '')
  }, [modelsData])

  return (
    <>
      <FormRow itemsCount={2}>
        <SelectFormControl
          name='vehicle.znacka'
          control={control}
          label='Značka' isRequired={makeRequired} isDisabled={isDisabled}
          onChange={(v) => onMakeChange(v as string)}
          valueTransform={(val) => val?.toString().toUpperCase()}
          isLoading={isLoadingMakers}
          unselectedText='Vyberte značku vozidla'
          options={makersData?.map((item) => ({ value: item.toUpperCase(), label: item.toUpperCase() }))}
          helperText={makeRequired && 'Zadejte tovární značku vozidla. V případě, že příslušná tovární značka není ve výběru, kontaktujte správce systému s žádostí o její přidání do číselníku.'}
        />

        {showMakeText &&
          <InputFormControl
            name='vehicle.znackaText'
            control={control}
            label='Tovární značka (text)'
            isDisabled={isDisabled}
            autoComplete='off'
            helperText="Zadejte tovární značku, pouze pokud nebyla vybrána ve výběru"
          />
        }
      </FormRow>

      <FormRow>
        <SelectFormControl
          name='vehicle.zakladniModel'
          control={control}
          label='Základní model'
          onChange={(v) => onModelBaseChange(v as string)}
          isDisabled={isDisabled || !make || !modelBaseData?.length}
          isLoading={usLoadingModelBases}
          isRequired={tooMuchModels}
          unselectedText={tooMuchModels ? 'Vyberte základní model' : ' '}
          options={modelBaseData?.map((item) => ({ value: item, label: item }))}
          helperText={tooMuchModels ? 'Pro vybranou značku je příliš mnoho možností k výběru. Prosím omezte počet výběrem základního modelu.' : 'Toto je pomocné pole pro výběr modelu vozidla a nemusí být vyplněno.'}
        />

        {useBrokerMakeModel.current
          ? <SelectFormControl
            name='vehicle.brokerMakeModelId'
            control={control}
            label='Model'
            isDisabled={isDisabled || (tooMuchModels && !modelBase)}
            isLoading={isLoadingModels}
            isRequired
            unselectedText='Vyberte model'
            options={modelOptions}
            onChange={(v) => {
              const selectedModel = modelsData?.find(item => item.brokerMakeModelId == v)
              setValue('vehicle.model', selectedModel?.model ?? '')
              setValue('vehicle.zakladniModel', selectedModel?.modelBase)
            }}
            helperText="Zadejte model vozidla."
            valueAsNumber
          />
          : <SelectFormControl
            name='vehicle.model'
            control={control}
            label='Model'
            isDisabled={isDisabled || (tooMuchModels && !modelBase)}
            isLoading={isLoadingModels}
            isRequired
            unselectedText='Vyberte model'
            options={modelOptions}
            helperText="Zadejte model vozidla."
          />
        }

      </FormRow>
    </>
  )
}
